import React, { useState, useEffect } from "react";
import * as S from "./ChatInterfaceStyles";
import { FormSpec } from "./MessagePreprocessor";
import { FormContainer } from "./ChatInterfaceStyles"; // Import FormContainer

interface DynamicFormProps {
  form: FormSpec & { formForSchedule?: boolean };
  onSubmit: (values: Record<string, any>) => void;
  onCancel?: () => void;
  candidateForInterview?: any;
}

const DynamicForm: React.FC<DynamicFormProps> = ({ form, onSubmit, onCancel, candidateForInterview }) => {
  const [values, setValues] = useState<Record<string, any>>(() => {
    const initialValues: Record<string, any> = {};
    form.fields.forEach(item => {
      if ('fields' in item) { // FormRow
        item.fields.forEach(field => {
          if (field.defaultValue) {
            initialValues[field.name] = field.defaultValue;
          }
        });
      } else { // FormField
        if (item.defaultValue) {
          initialValues[item.name] = item.defaultValue;
        }
      }
    });
    return initialValues;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (form.formForSchedule && candidateForInterview) {
      setValues(prevValues => ({
        ...prevValues,
        candidate_name: candidateForInterview[0],
        candidate_email: candidateForInterview[3]
      }));
    }
  }, [candidateForInterview, form.formForSchedule]);

  const handleBlur = (field: any) => {
    if (field.type === "email") {
      if (values[field.name] && !validateEmail(values[field.name])) {
        setErrors(prev => ({ ...prev, [field.name]: "Invalid email format" }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field.name];
          return newErrors;
        });
      }
    }
  };
  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    let hasErrors = false;

    form.fields.forEach((item: any) => {
      const processField = (field: any) => {
        if (field.type === 'email') {
          if (!values[field.name] || !validateEmail(values[field.name])) {
            newErrors[field.name] = 'Invalid email format';
            hasErrors = true;
          }
        }
      };

      if (item && item.layout === "row" && Array.isArray(item.fields)) {
        item.fields.forEach(processField);
      } else {
        processField(item);
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit(values);
    }
  };

  return (
    <FormContainer>
      <form onSubmit={handleSubmit}>
        {form.title && <h4>{form.title}</h4>}
        <div className="grid">
          {form.fields.flatMap((item: any, idx: number) => {
            if (item && item.layout === "row" && Array.isArray(item.fields)) {
              return item.fields.map((field: any, subIdx: number) => {
                const span = Math.min(Math.max(field.span || 6, 1), 12);
                const style = { gridColumn: `span ${span}` } as React.CSSProperties;
                const key = `${idx}-${subIdx}`;
                return (
                  <div className="grid-item" style={style} key={key}>
                    {field.type === "select" ? (
                      <>
                        <label>{field.label}</label>
                        <select
                          defaultValue={field.defaultValue}
                          onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                          required={field.required}
                        >
                          <option value="">Select</option>
                          {field.options.map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </>
                    ) : field.type === "textarea" ? (
                      <>
                        <label>{field.label}</label>
                        <textarea
                          defaultValue={field.defaultValue}
                          placeholder={field.placeholder}
                          onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                          required={field.required}
                        />
                      </>
                    ) : (
                      <>
                        <label>{field.label}</label>
                        <input
                          value={values[field.name] || ''}
                          readOnly={
                            form.formForSchedule && (field.name === 'candidate_name' || field.name === 'candidate_email')
                          }
                          type={field.type === "text" ? "text" : field.type}
                          placeholder={field.placeholder}
                          onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                          onBlur={() => handleBlur(field)}
                          required={field.required}
                          min={field.type === "date" ? today : undefined}
                        />
                        {errors[field.name] && <small style={{ color: 'red' }}>{errors[field.name]}</small>}
                      </>
                    )}
                  </div>
                );
              });
            }

            const field = item;
            const span = Math.min(Math.max(field.span || (field.layout === "half" ? 6 : 12), 1), 12);
            const style = { gridColumn: `span ${span}` } as React.CSSProperties;
            return (
              <div className="grid-item" style={style} key={idx}>
                {field.type === "select" ? (
                  <>
                    <label>{field.label}</label>
                    <select
                      defaultValue={field.defaultValue}
                      onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                      required={field.required}
                    >
                      <option value="">Select</option>
                      {field.options.map((opt: string) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </>
                ) : field.type === "textarea" ? (
                  <>
                    <label>{field.label}</label>
                    <textarea
                      defaultValue={field.defaultValue}
                      placeholder={field.placeholder}
                      onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                      required={field.required}
                    />
                  </>
                ) : (
                  <>
                    <label>{field.label}</label>
                    <input
                      value={values[field.name] || ''}
                      readOnly={
                        form.formForSchedule && (field.name === 'candidate_name' || field.name === 'candidate_email')
                      }
                      type={field.type === "text" ? "text" : field.type}
                      placeholder={field.placeholder}
                      onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}
                      onBlur={() => handleBlur(field)}
                      required={field.required}
                      min={field.type === "date" ? today : undefined}
                    />
                    {errors[field.name] && <small style={{ color: 'red' }}>{errors[field.name]}</small>}
                  </>
                )}
              </div>
            );
          })}
        </div>
        <button type="submit">{form.submitLabel || "Submit"}</button>
        {onCancel && <button type="button" onClick={onCancel}>Cancel</button>}
      </form>
    </FormContainer>
  );
};

export default DynamicForm;
